import { Injectable } from '@angular/core';
import { defer, iif, merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { AudioDevicesSettingsSelectors } from '../../store/local-storage/local-storage-settings/audio-devices-settings.selectors';
import _ from 'lodash';
import { DEVICE_ID_ALL, DEVICE_ID_DEFAULT } from './audio.constancts';
import { JabraService } from '../headset/jabra.service';
import { PlantronicsService } from '../headset/plantronics.service';
import { SennheiserService } from '../headset/sennheiser.service';

declare global {
   interface HTMLAudioElement {
      sinkId: string;

      setSinkId(sinkId: string): Promise<any>;
   }
}

@Injectable({
   providedIn: 'root',
})
export class AudioService {
   constructor(
      private store: Store,
      public jabraService: JabraService,
      public plantronicsService: PlantronicsService,
      public sennheiserService: SennheiserService
   ) {}

   public availableDevices$: Observable<MediaDeviceInfo[]>;

   public availableRingerDevices$: Observable<MediaDeviceInfo[]>;

   public availableAudioOutputDevices$: Observable<MediaDeviceInfo[]>;

   public availableAudioInputDevices$: Observable<MediaDeviceInfo[]>;

   public chosenRingerDevices$: Observable<MediaDeviceInfo[]>;

   public chosenAudioOutputDevice$: Observable<MediaDeviceInfo>;

   public chosenAudioInputDevice$: Observable<MediaDeviceInfo>;

   /**
    * Checks if the API for set an audio output device is available.
    */
   public isSettingOutputAudioDeviceAvailable(): boolean {
      const tempAudioElement: HTMLAudioElement = document.createElement('audio');
      return tempAudioElement !== undefined && tempAudioElement.sinkId !== undefined && tempAudioElement.setSinkId !== undefined;
   }

   public initializeAudioService(): void {
      this.jabraService.init();
      this.plantronicsService.init();
      this.sennheiserService.init();

      const deviceConstraints: MediaStreamConstraints = {
         audio: true,
         video: false,
      };

      const deviceChanged$ = new Subject<any>();
      navigator.mediaDevices.ondevicechange = () => deviceChanged$.next(null);

      // wait for user to accept access (and release media stream afterwards) or device changes
      this.availableDevices$ = merge(
         defer(() => navigator.mediaDevices.getUserMedia(deviceConstraints)).pipe(
            tap((ms) => {
               ms.getTracks().forEach((track) => track.stop());
            })
         ),
         deviceChanged$
      ).pipe(
         switchMap(() => navigator.mediaDevices.enumerateDevices()),
         shareReplay(1)
      );

      this.availableAudioInputDevices$ = this.availableDevices$.pipe(
         map((d) => d.filter((devInfo) => devInfo.kind === 'audioinput')),
         // as the ondevicechange is normally called twice (once for input and once for the output device)
         // we emmit "duplicate changes" by comparing the stringyfied representation (dirty but working code)
         distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      );

      this.availableAudioOutputDevices$ = this.availableDevices$.pipe(
         map((d) => d.filter((devInfo) => devInfo.kind === 'audiooutput')),
         // as the ondevicechange is normally called twice (once for input and once for the output device)
         // we emmit "duplicate changes" by comparing the stringyfied representation (dirty but working code)
         distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      );

      const allDevicesDeviceInfo: MediaDeviceInfo = {
         deviceId: DEVICE_ID_ALL,
         kind: 'audiooutput',
         groupId: '',
         label: 'common.audio_settings.devices.all',
         // toJSON should not be required but there seems to be an error in the interface definition
         // see: https://github.com/microsoft/TypeScript/issues/36202
         toJSON: () => {
            return {
               deviceId: DEVICE_ID_ALL,
               kind: 'audiooutput',
               groupId: '',
               label: 'common.audio_settings.devices.all',
            };
         },
      };

      this.availableRingerDevices$ = this.availableAudioOutputDevices$.pipe(map((x) => [allDevicesDeviceInfo, ...x]));

      this.chosenAudioInputDevice$ = this.store.select(AudioDevicesSettingsSelectors.getAudioInputDeviceId).pipe(
         switchMap((inputDeviceId) =>
            this.availableDevices$.pipe(
               map((availableDevices) => {
                  const device = _(availableDevices)
                     .filter((x) => x.deviceId === inputDeviceId)
                     .head();
                  if (_.isNil(device)) {
                     return _.head(availableDevices);
                  }
                  return device;
               })
            )
         )
      );

      this.chosenAudioOutputDevice$ = this.store.select(AudioDevicesSettingsSelectors.getAudioOutputDeviceId).pipe(
         switchMap((inputDeviceId) =>
            this.availableAudioOutputDevices$.pipe(
               map((availableDevices) => {
                  const device = _(availableDevices)
                     .filter((x) => x.deviceId === inputDeviceId)
                     .head();
                  if (_.isNil(device)) {
                     return _.head(availableDevices);
                  }
                  return device;
               })
            )
         )
      );

      this.chosenRingerDevices$ = this.store.select(AudioDevicesSettingsSelectors.getRingerDeviceId).pipe(
         switchMap((outputDeviceId) =>
            iif(
               () => outputDeviceId === DEVICE_ID_ALL,
               this.availableAudioOutputDevices$.pipe(map((x) => x.filter((y) => y.deviceId !== DEVICE_ID_DEFAULT))),
               this.availableDevices$.pipe(
                  map((availableDevices) => {
                     const devices = _(availableDevices)
                        .filter((x) => x.deviceId === outputDeviceId)
                        .value();
                     if (_.isEmpty(devices)) {
                        return [_.head(availableDevices)];
                     }
                     return devices;
                  })
               )
            )
         ),
         map((devices) =>
            _.map(
               _.groupBy(devices, (x) => x.deviceId),
               (x) => x[0]
            )
         )
      );
   }
}
